import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Chip,
  Stack,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Alert,
} from '@mui/material';
import { Settings, School as SchoolIcon, GroupAdd, Lock, Sync as SyncIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { TableStyle } from '../../resources/styling';
import { sa, PillButton } from '../superAdminUi';
import ModalShell from './ModalShell';
import wondeService from '../../services/api/wondeService';
import WondeManageModal from './WondeManageModal';
import {
  getStatusLabel,
  getStatusColor,
  isApproved,
  isPending,
  canRequestConnection,
} from '../../utils/wondeConnectionStatus';

// A school can be bulk-requested only if it isn't already pending/approved AND has a URN to resolve by.
const isEligible = (s) => canRequestConnection(s.Status) && s.Urn > 0;

// An already-connected school can have its sync settings bulk-edited in place.
const isSyncEditable = (s) => isApproved(s.Status);

// Rows that are neither requestable nor connected (e.g. no URN, pending) stay locked.
const isRowActionable = (s) => isEligible(s) || isSyncEditable(s);

/**
 * Wonde connection status + entry point. Replaces the old WondeConnectionSummary
 * / WondeSyncModal surface with the SchoolId-keyed redesign:
 *   - Single school → status chip + "Manage Wonde" → WondeManageModal
 *   - MAT/group     → "n/m approved" summary + a per-school list with bulk request
 *                     (per-row select + per-row sync) and drill-in to the manage modal.
 */
const WondeConnectionPanel = ({ schoolId, matId, isMatLevel = false }) => {
  const [loading, setLoading] = useState(false);
  const [connection, setConnection] = useState(null); // single school dto
  const [groupConnections, setGroupConnections] = useState(null); // array of dto
  const [manageSchoolId, setManageSchoolId] = useState(null);
  const [groupOpen, setGroupOpen] = useState(false);

  // bulk actions — per-school selection: { [schoolId]: { selected, staff, student } }
  const [sel, setSel] = useState({});
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkSyncSaving, setBulkSyncSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const fetchData = useCallback(async () => {
    if (!schoolId && !matId) return;
    setLoading(true);
    try {
      if (isMatLevel && matId) {
        const res = await wondeService.getGroupConnections(matId);
        if (res && (res.success || res.Success)) {
          const list = res.data || res.Data || [];
          setGroupConnections(Array.isArray(list) ? list : []);
        } else {
          setGroupConnections(null); // null = unknown (don't render as "No schools")
        }
      } else if (schoolId) {
        const res = await wondeService.getConnection(schoolId);
        setConnection(
          res && (res.success || res.Success) ? res.data || res.Data || null : null
        );
      }
    } catch (e) {
      // Leave as null so the UI renders an explicit "unknown" state, not fake data.
      if (isMatLevel) setGroupConnections(null);
      else setConnection(null);
    } finally {
      setLoading(false);
    }
  }, [isMatLevel, matId, schoolId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Initialise the bulk selection whenever the group list (re)loads. Requestable rows pre-select with
  // the single-school connect defaults (student on, staff off); connected rows pre-select and seed
  // their toggles from the school's live sync state so bulk-edit starts from the truth, not a guess.
  useEffect(() => {
    if (!groupConnections) return;
    const next = {};
    groupConnections.forEach((s) => {
      next[s.SchoolId] = isSyncEditable(s)
        ? {
            selected: true,
            staff: Boolean(s.StaffSyncActive),
            student: Boolean(s.StudentParentSyncActive),
          }
        : { selected: isEligible(s), staff: false, student: true };
    });
    setSel(next);
  }, [groupConnections]);

  const handleBulkRequest = async (eligibleList) => {
    const schools = eligibleList
      .filter((s) => sel[s.SchoolId]?.selected)
      .map((s) => ({
        schoolId: s.SchoolId,
        staffSync: Boolean(sel[s.SchoolId]?.staff),
        studentParentSync: Boolean(sel[s.SchoolId]?.student),
      }));
    if (schools.length === 0) return;

    setBulkRunning(true);
    try {
      const res = await wondeService.bulkRequest(matId, schools);
      const data = res && (res.data || res.Data);
      const msg = (res && (res.message || res.Message)) || '';
      if (data) {
        // We got a per-school summary — always show it. success === false just means some rows
        // failed/were skipped (the alert lists them), not that the call itself failed.
        setBulkResult(data);
        if (res.success || res.Success) {
          toast.success(msg || 'Bulk request complete.');
        } else {
          toast.warning(msg || 'Some requests need attention — see the summary.');
        }
        await fetchData();
      } else {
        toast.error(msg || 'Bulk request failed.');
      }
    } catch (e) {
      toast.error(e?.message || 'Bulk request failed.');
    } finally {
      setBulkRunning(false);
    }
  };

  const handleBulkSaveSync = async (approvedList) => {
    const schools = approvedList
      .filter((s) => sel[s.SchoolId]?.selected)
      .map((s) => ({
        schoolId: s.SchoolId,
        staffSync: Boolean(sel[s.SchoolId]?.staff),
        studentParentSync: Boolean(sel[s.SchoolId]?.student),
      }));
    if (schools.length === 0) return;

    setBulkSyncSaving(true);
    try {
      const res = await wondeService.bulkSaveSyncSettings(matId, schools);
      const data = res && (res.data || res.Data);
      const msg = (res && (res.message || res.Message)) || '';
      if (data) {
        setBulkResult(data);
        if (res.success || res.Success) {
          toast.success(msg || 'Sync settings saved.');
        } else {
          toast.warning(msg || 'Some schools need attention — see the summary.');
        }
        await fetchData();
      } else {
        toast.error(msg || 'Saving sync settings failed.');
      }
    } catch (e) {
      toast.error(e?.message || 'Saving sync settings failed.');
    } finally {
      setBulkSyncSaving(false);
    }
  };

  if (loading && !connection && !groupConnections) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <span>Loading Wonde status…</span>
      </Box>
    );
  }

  // ---- MAT/group ----
  if (isMatLevel) {
    const list = groupConnections || [];
    const approved = list.filter((s) => isApproved(s.Status)).length;
    const eligibleList = list.filter(isEligible); // requestable
    const syncEditableList = list.filter(isSyncEditable); // connected → editable sync
    const actionableList = list.filter(isRowActionable); // selectable rows (request or sync)

    const requestSelectedCount = eligibleList.filter((s) => sel[s.SchoolId]?.selected).length;
    const syncSelectedCount = syncEditableList.filter((s) => sel[s.SchoolId]?.selected).length;
    const selectedCount = actionableList.filter((s) => sel[s.SchoolId]?.selected).length;

    const allSelected = actionableList.length > 0 && selectedCount === actionableList.length;
    const someSelected = selectedCount > 0 && !allSelected;
    const lockedCount = list.length - actionableList.length;

    // "Set all" header state — true only when every *selected* actionable row has that sync type on.
    const selectedActionable = actionableList.filter((s) => sel[s.SchoolId]?.selected);
    const allStaff = selectedActionable.length > 0 && selectedActionable.every((s) => sel[s.SchoolId]?.staff);
    const allStudent =
      selectedActionable.length > 0 && selectedActionable.every((s) => sel[s.SchoolId]?.student);

    const setRow = (id, patch) => setSel((p) => ({ ...p, [id]: { ...(p[id] || {}), ...patch } }));
    const toggleSelectAll = () =>
      setSel((p) => {
        const next = { ...p };
        actionableList.forEach((s) => {
          next[s.SchoolId] = { ...(next[s.SchoolId] || {}), selected: !allSelected };
        });
        return next;
      });
    const setAllSync = (key, value) =>
      setSel((p) => {
        const next = { ...p };
        actionableList.forEach((s) => {
          if (next[s.SchoolId]?.selected) next[s.SchoolId] = { ...(next[s.SchoolId] || {}), [key]: value };
        });
        return next;
      });

    const summary =
      groupConnections === null
        ? { label: 'Status unknown', color: 'default' }
        : list.length === 0
        ? { label: 'No schools', color: 'default' }
        : approved === list.length
        ? { label: `All ${list.length} approved`, color: 'success' }
        : approved === 0
        ? { label: `${list.length} schools — none approved`, color: 'default' }
        : { label: `${approved}/${list.length} approved`, color: 'warning' };

    const reasonFor = (s) => {
      if (isEligible(s)) return null;
      if (isApproved(s.Status)) return 'connected — edit sync below';
      if (isPending(s.Status)) return 'already requested';
      if (!(s.Urn > 0)) return 'no URN — connect individually';
      return 'not eligible';
    };

    return (
      <>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip icon={<SchoolIcon />} label={summary.label} color={summary.color} />
          <PillButton type="button" $variant="teal" $size="lg" onClick={() => setGroupOpen(true)}>
            <Settings sx={{ fontSize: 16 }} /> Manage Wonde
          </PillButton>
        </Stack>

        <ModalShell
          open={groupOpen}
          onClose={() => setGroupOpen(false)}
          title="Wonde connections — trust schools"
          titleId="wonde-trust-modal-title"
          width={{ xs: '94%', md: 880 }}
        >
          <>
            {bulkResult && (
              <Alert
                severity={bulkResult.Failed > 0 ? 'warning' : 'success'}
                sx={{ mb: 2 }}
                onClose={() => setBulkResult(null)}
              >
                {/* The request and sync-save summaries share a shape; only the "actioned" verb differs. */}
                {bulkResult.Updated != null
                  ? `${bulkResult.Updated} updated, ${bulkResult.Skipped} skipped, ${bulkResult.Failed} failed.`
                  : `${bulkResult.Requested} requested, ${bulkResult.Skipped} skipped, ${bulkResult.Failed} failed.`}
                {(bulkResult.Results || [])
                  .filter((r) => r.Outcome !== 'Requested' && r.Outcome !== 'Updated' && r.Message)
                  .slice(0, 8)
                  .map((r) => (
                    <div key={r.SchoolId} style={{ fontSize: 12 }}>
                      • {r.SchoolName}: {r.Message}
                    </div>
                  ))}
              </Alert>
            )}

            <TableStyle>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={toggleSelectAll}
                        disabled={actionableList.length === 0}
                        inputProps={{ 'aria-label': 'Select all actionable' }}
                      />
                    </TableCell>
                    <TableCell>School</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">
                      Staff
                      <br />
                      <Checkbox
                        size="small"
                        checked={allStaff}
                        disabled={selectedCount === 0}
                        onChange={() => setAllSync('staff', !allStaff)}
                        inputProps={{ 'aria-label': 'Set staff sync for all selected' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      Students
                      <br />
                      <Checkbox
                        size="small"
                        checked={allStudent}
                        disabled={selectedCount === 0}
                        onChange={() => setAllSync('student', !allStudent)}
                        inputProps={{ 'aria-label': 'Set student sync for all selected' }}
                      />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.map((s) => {
                    const actionable = isRowActionable(s);
                    const row = sel[s.SchoolId] || {};
                    const reason = reasonFor(s);
                    return (
                      <TableRow key={s.SchoolId} sx={!actionable ? { opacity: 0.6 } : undefined}>
                        <TableCell padding="checkbox">
                          {actionable ? (
                            <Checkbox
                              size="small"
                              checked={Boolean(row.selected)}
                              onChange={() => setRow(s.SchoolId, { selected: !row.selected })}
                              inputProps={{ 'aria-label': `Select ${s.SchoolName}` }}
                            />
                          ) : (
                            <Lock fontSize="small" sx={{ color: sa.color.textGray }} />
                          )}
                        </TableCell>
                        <TableCell>
                          <div>{s.SchoolName || '—'}</div>
                          <div style={{ fontSize: 11, color: sa.color.textGray }}>
                            {[s.Urn ? `URN ${s.Urn}` : null, reason].filter(Boolean).join(' · ')}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={s.StatusLabel || getStatusLabel(s.Status)}
                            color={getStatusColor(s.Status)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {actionable ? (
                            <Checkbox
                              size="small"
                              checked={Boolean(row.staff)}
                              disabled={!row.selected}
                              onChange={() => setRow(s.SchoolId, { staff: !row.staff })}
                              inputProps={{ 'aria-label': `Staff sync for ${s.SchoolName}` }}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {actionable ? (
                            <Checkbox
                              size="small"
                              checked={Boolean(row.student)}
                              disabled={!row.selected}
                              onChange={() => setRow(s.SchoolId, { student: !row.student })}
                              inputProps={{ 'aria-label': `Student sync for ${s.SchoolName}` }}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          <PillButton type="button" $variant="outline" onClick={() => setManageSchoolId(s.SchoolId)}>
                            Manage
                          </PillButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {list.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>No schools found for this trust.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableStyle>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 3,
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Typography variant="caption" sx={{ color: sa.color.textGray }}>
                {selectedCount} selected
                {lockedCount > 0 ? ` · ${lockedCount} locked (pending / no URN)` : ''}
              </Typography>
              <Stack direction="row" spacing={1}>
                <PillButton
                  type="button"
                  $variant="teal"
                  disabled={requestSelectedCount === 0 || bulkRunning || bulkSyncSaving}
                  onClick={() => handleBulkRequest(eligibleList)}
                >
                  {bulkRunning ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <>
                      <GroupAdd sx={{ fontSize: 16 }} /> Request access ({requestSelectedCount})
                    </>
                  )}
                </PillButton>
                <PillButton
                  type="button"
                  $variant="teal"
                  disabled={syncSelectedCount === 0 || bulkSyncSaving || bulkRunning}
                  onClick={() => handleBulkSaveSync(syncEditableList)}
                >
                  {bulkSyncSaving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <>
                      <SyncIcon sx={{ fontSize: 16 }} /> Save sync ({syncSelectedCount})
                    </>
                  )}
                </PillButton>
                <PillButton type="button" $variant="outline" onClick={() => setGroupOpen(false)}>
                  Close
                </PillButton>
              </Stack>
            </Box>
          </>
        </ModalShell>

        <WondeManageModal
          open={Boolean(manageSchoolId)}
          schoolId={manageSchoolId}
          onClose={() => setManageSchoolId(null)}
          onChanged={fetchData}
        />
      </>
    );
  }

  // ---- single school ----
  const status = connection?.Status;
  const chipLabel = connection
    ? connection.StatusLabel || getStatusLabel(status)
    : 'Status unknown';
  const chipColor = connection ? getStatusColor(status) : 'default';

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip icon={<SchoolIcon />} label={chipLabel} color={chipColor} />
        <PillButton type="button" $variant="teal" $size="lg" onClick={() => setManageSchoolId(schoolId)}>
          <Settings sx={{ fontSize: 16 }} /> Manage Wonde
        </PillButton>
      </Stack>

      <WondeManageModal
        open={Boolean(manageSchoolId)}
        schoolId={manageSchoolId}
        onClose={() => setManageSchoolId(null)}
        onChanged={fetchData}
      />
    </>
  );
};

export default WondeConnectionPanel;
